// src/components/alerts/alertUtils.js
// --- UPDATE (Alert System AI) — NEW: shared constants and helpers for every alert component
// (severity / status colours were copied into five files with slightly different colours).

export const SEVERITIES = ['critical', 'high', 'medium', 'low'];
export const OPEN_STATUSES = ['active', 'acknowledged', 'work_order_created'];

export const ALERT_STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'work_order_created', label: 'Work order created' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'auto_resolved', label: 'Auto-resolved' },
];
export const SEVERITY_OPTIONS = SEVERITIES.map(s => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }));

const SEVERITY_COLOR = { critical: 'red', high: 'orange', medium: 'yellow', low: 'blue' };
const STATUS_COLOR = {
    active: 'red',
    acknowledged: 'blue',
    work_order_created: 'purple',
    resolved: 'green',
    auto_resolved: 'teal',
    resolved_by_work_order: 'green',
};

export const severityColor = (s) => SEVERITY_COLOR[String(s || '').toLowerCase()] || 'gray';
export const statusColor = (s) => STATUS_COLOR[String(s || '').toLowerCase()] || 'gray';
export const statusLabel = (s) => String(s || 'unknown').replace(/_/g, ' ');
export const isOpenAlert = (a) => OPEN_STATUSES.includes(String(a?.status || '').toLowerCase());

/** FlowSense AI triage score → colour scheme. */
export const scoreColor = (score) => (score >= 70 ? 'red' : score >= 45 ? 'orange' : score >= 25 ? 'yellow' : 'green');
export const healthColor = (score) => (score >= 85 ? 'green' : score >= 65 ? 'yellow' : score >= 40 ? 'orange' : 'red');

export const fmtMinutes = (m) => {
    if (m === null || m === undefined || Number.isNaN(Number(m))) return '—';
    const v = Number(m);
    if (v < 1) return '< 1 min';
    if (v < 60) return `${Math.round(v)} min`;
    if (v < 1440) return `${(v / 60).toFixed(1)} h`;
    return `${(v / 1440).toFixed(1)} d`;
};

export const timeAgo = (ts, now = Date.now()) => {
    if (!ts) return '—';
    const diff = Math.max(0, now - new Date(ts).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

/** CSV cell: quotes, commas and new lines are escaped (the old export broke on them). */
export const csvEscape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (headers, rows) => [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');

export const downloadFile = (filename, content, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** A short beep through Web Audio (works after any click on the page; no audio file needed). */
let _audioCtx = null;
export const playAlertTone = (severity = 'critical') => {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        _audioCtx = _audioCtx || new Ctx();
        const ctx = _audioCtx;
        const tones = severity === 'critical' ? [880, 660, 880] : [660];
        tones.forEach((f, i) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine';
            o.frequency.value = f;
            const t = ctx.currentTime + i * 0.18;
            g.gain.setValueAtTime(0.0001, t);
            g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
            o.connect(g).connect(ctx.destination);
            o.start(t);
            o.stop(t + 0.17);
        });
    } catch (_) { /* audio blocked — ignore */ }
};

export const readLocal = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (_) { return fallback; }
};
export const writeLocal = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* private mode */ }
};

/** WebSocket messages arrive as { type, payload } or as the payload itself. */
export const wsPayload = (message) => (message && (message.payload || message.data)) || message || {};
// --- END UPDATE
